import { useEffect, useState } from "react";

import { Link } from "../../../../utils/routing";
import { pageUrl } from "../../../../utils/urls";

import { PageLink } from "./PageLink";
import { PublishStatus } from "./PublishStatus";
import { useFetchAssessmentBaseData, useFetchAssessmentData } from "./assessment-data";
import { AssessmentDashboardDataType, useAssessmentDashboardData } from "./hooks";
import { getAssessmentStatusLabel } from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import { AssessmentDataTable } from "components/materials/course/assessment/AssessmentDataTable";
import {
  MarkAssessmentCompleteMenu,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "components/materials/course/assessment/helpers";
import { AssessedCount } from "components/materials/page/assessment/AssessedCount";
import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Tooltip } from "mds/components/Tooltip";
import { CircleInfoIcon, ScoreIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentCourse, selectCurrentCourseId } from "store/selectors";
import { getUserFullName } from "utils/user-utils";

export const CourseAssessmentView = () => {
  const course = useAppSelector(selectCurrentCourse);
  const courseId = useAppSelector(selectCurrentCourseId);

  const {
    allAssessmentsCSVData,
    allAssessmentsTableRows,
    studentPointsPerPageCSVData,
    studentPointsPerPageTableRows,
    studentScoresPerOutcomeCSVData,
    studentScoresPerOutcomeTableRows,
    studentCourseGradesTableRows,
    studentCourseGradesCSVData,
    courseStudents,
    courseOutcomes,
    publishedPagesWithAssessments,
  }: AssessmentDashboardDataType = useAssessmentDashboardData();

  useFetchAssessmentBaseData();
  useFetchAssessmentData();

  useEffect(() => {
    if (courseId) {
      storeApi.page_outcomes.list({ course_id: courseId });
      storeApi.page_group_users.list({ course_id: courseId });
    }
  }, [courseId]);

  const [, setDummyData] = useState({});
  const refetch = () => setDummyData({}); // Dummy state update to trigger refetch

  const activitiesMaxValue = course?.course_grade_activities_percentage ?? 0;
  const prepWorkMaxValue = course?.course_grade_prepwork_percentage ?? 0;
  const assignmentsMaxValue = course?.course_grade_assignments_percentage ?? 0;

  return (
    <main className="assessment-dashboard">
      {studentCourseGradesTableRows.length > 0 && (
        <AssessmentDataTable
          exportData={studentCourseGradesCSVData}
          title={t("assessment_dashboard.student_course_grades.title")}
        >
          <Table>
            {/* Headers: Student / Prep Work / In-Class Work / Assignments / Total Course Grade */}
            <THead>
              <TR className="border-black-tint-70">
                <TH className="w-16 pl-4">
                  {t("assessment_dashboard.all.student_name_header_title")}
                </TH>

                {!!prepWorkMaxValue && (
                  <TH className="w-16">{t("assessment_dashboard.grades.prepwork")}</TH>
                )}

                {!!activitiesMaxValue && (
                  <TH className="w-16">{t("assessment_dashboard.grades.activities")}</TH>
                )}

                {!!assignmentsMaxValue && (
                  <TH className="w-16">{t("assessment_dashboard.grades.assignments")}</TH>
                )}

                <TH className="w-16">
                  <Tooltip text={t("assessment_dashboard.tooltip.course_score")}>
                    {t(
                      "assessment_dashboard.student_course_grades.total_course_grade_header_title",
                    )}
                  </Tooltip>
                </TH>
              </TR>
            </THead>

            <tbody className="font-normal">
              {studentCourseGradesTableRows.map((row) => (
                <TR key={row.id}>
                  <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.studentName}>
                    {row.studentName}
                  </TD>

                  {!!prepWorkMaxValue && <TD>{row.prepWorkPercentScore}</TD>}

                  {!!activitiesMaxValue && <TD>{row.activitiesPercentScore}</TD>}

                  {!!assignmentsMaxValue && <TD>{row.assignmentsPercentScore}</TD>}

                  <TD>{row.totalCourseGradeForDisplayIncludingMaxValue}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </AssessmentDataTable>
      )}

      <AssessmentDataTable
        exportData={allAssessmentsCSVData}
        title={t("assessment_dashboard.all.title")}
      >
        <Table>
          {/* Headers: Topic / Page / Status / Publishing / Assessed / Page Type /Avg. Points / Outcomes / Last Submitted */}
          <THead>
            <TR className="border-black-tint-70">
              <TH className="w-6/12 pl-4">
                {t("assessment_dashboard.all.page_topic_header_title")}
              </TH>

              <TH className="w-6/12">{t("assessment_dashboard.all.page_header_title")}</TH>

              <TH className="w-16">{t("assessment_dashboard.all.status_header_title")}</TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.publishing_header_tooltip")}>
                  {t("assessment_dashboard.all.publishing_header_title")}
                </Tooltip>
              </TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.assessed_header_tooltip")}>
                  {t("assessment_dashboard.all.assessed_header_title")}
                </Tooltip>
              </TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                  {t("assessment_dashboard.all.page_type_header_title")}
                </Tooltip>
              </TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                  {t("assessment_dashboard.all.avg_points_header_title")}
                </Tooltip>
              </TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.outcomes_header_tooltip")}>
                  {t("assessment_dashboard.all.outcomes_header_title")}
                </Tooltip>
              </TH>

              <TH className="w-16">
                <Tooltip text={t("assessment_dashboard.all.last_submitted_header_tooltip")}>
                  {t("assessment_dashboard.all.last_submitted_header_title")}
                </Tooltip>
              </TH>
            </TR>
          </THead>

          <tbody>
            {allAssessmentsTableRows.map((row) => (
              <TR key={row.id}>
                <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                  {row.topicTitle || "–"}
                </TD>

                <TD className="max-w-40 truncate" title={row.page.title}>
                  <PageLink
                    className="w-full truncate text-blue-shade-20 underline"
                    page={row.page}
                  />
                </TD>

                <TD className="max-w-40 truncate">
                  {!row.status || row.status === AssessmentStatus.NOT_ASSESSABLE ? (
                    "-"
                  ) : (
                    <Button
                      as={Link}
                      className="gap-1 text-xs"
                      eventName={`Course - All Assessments / ${row.isAssessmentComplete || row.isMarkedComplete ? "View assessed page" : "Start assessing"}`}
                      eventProps={{
                        pageTitle: row.page.title,
                        pageUrl: pageUrl(courseId, row.page.id),
                        pageId: row.page.id,
                      }}
                      kind={row.isAssessmentComplete || row.isMarkedComplete ? "green" : "blue"}
                      size="xs"
                      to={pageUrl(courseId, row.page.id, {
                        additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
                      })}
                    >
                      {getAssessmentStatusLabel(
                        row.isMarkedComplete,
                        row.assessedSubmissionsCount,
                        row.isAssessmentComplete,
                      )}
                      {row.unassessedLateCount > 0 && (
                        <Tooltip
                          text={t("assessment.late_assessment_count", {
                            count: row.unassessedLateCount,
                          })}
                        >
                          <CircleInfoIcon />
                        </Tooltip>
                      )}
                    </Button>
                  )}
                </TD>

                <TD>
                  <PublishStatus status={row.publishing === "Published" ? "published" : "draft"} />
                </TD>

                <TD>
                  <AssessedCount page={row.page} />
                </TD>

                <TD>{row.pageType}</TD>

                <TD>{row.avgPoints}</TD>

                <TD>
                  <Tooltip text={row.outcomesTooltip}>{row.outcomesCount}</Tooltip>
                </TD>

                <TD>{row.lastSubmittedDate}</TD>

                <td>
                  <MarkAssessmentCompleteMenu
                    courseId={courseId}
                    isMarkedComplete={Boolean(row.page.assessing_marked_complete_at)}
                    pageId={row.page.id}
                    pageTitle={row.page.title}
                    isCourseAssessment
                    onChange={refetch}
                  />
                </td>
              </TR>
            ))}
          </tbody>
        </Table>
      </AssessmentDataTable>

      {publishedPagesWithAssessments.length !== 0 && (
        <AssessmentDataTable
          exportData={studentPointsPerPageCSVData}
          title={t("assessment_dashboard.student_points_per_page_title")}
        >
          <Table>
            {/* Headers: Page / Page Type / Avg. Points / User A / User B / User C... */}
            {/* One row per page, showing each student's score on that page */}
            <THead>
              <TR className="border-black-tint-70">
                <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

                <TH className="w-16">
                  <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                    {t("assessment_dashboard.all.page_type_header_title")}
                  </Tooltip>
                </TH>

                <TH className="w-16">
                  <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                    {t("assessment_dashboard.all.avg_points_header_title")}
                  </Tooltip>
                </TH>

                {courseStudents.map((student) => (
                  <TH key={student.user_id} verticalDivTitle={getUserFullName(student)} isVertical>
                    {getUserFullName(student)}
                  </TH>
                ))}
              </TR>
            </THead>

            <tbody>
              {studentPointsPerPageTableRows.map((row) => (
                <TR key={row.id}>
                  <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.page.title}>
                    <PageLink
                      className="w-full truncate text-blue-shade-20 underline"
                      page={row.page}
                    />
                  </TD>

                  <TD>{row.pageType}</TD>

                  <TD>{row.avgPoints}</TD>

                  {row.studentScores.map((score, index) => (
                    <TD key={courseStudents[index].user_id}>{score}</TD>
                  ))}
                </TR>
              ))}
            </tbody>
          </Table>
        </AssessmentDataTable>
      )}

      <AssessmentDataTable
        exportData={studentScoresPerOutcomeCSVData}
        title={t("assessment_dashboard.student_score_per_outcome_title")}
        titleTooltipText={t("assessment_dashboard.student_score_per_outcome_tooltip")}
      >
        <Table>
          {/* Headers: <blank> / Outcome A / Outcome B / Outcome C */}
          {/* One row per student, showing each student's aggregate outcome scores */}
          <THead>
            <TR className="border-black-tint-70">
              <TH className="w-6/12 pl-4">
                {t("assessment_dashboard.all.student_name_header_title")}
              </TH>

              {courseOutcomes.map((courseOutcome) => (
                <TH
                  key={courseOutcome.id}
                  verticalDivClassName="max-h-[180px]"
                  verticalDivTitle={displayOutcomeName(courseOutcome.outcome)}
                  isVertical
                >
                  {displayOutcomeName(courseOutcome.outcome)}
                </TH>
              ))}
            </TR>
          </THead>

          <tbody>
            {studentScoresPerOutcomeTableRows.map((row) => (
              <TR key={row.student.user_id}>
                <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.studentName}>
                  {row.studentName}
                </TD>

                {row.outcomeScores.map((score, index) => (
                  <TD key={courseOutcomes[index].id}>
                    <ScoreIcon value={score ?? NaN} />
                  </TD>
                ))}
              </TR>
            ))}
          </tbody>
        </Table>
      </AssessmentDataTable>
    </main>
  );
};
